import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useClient } from '../../../app/api';
import {
    type LiquidityCalculatorCalculationDto,
    type LiquidityCalculatorPreferencesUpdateDto,
    updateLiquidityCalculatorCalculation,
} from '../../../app/api/generated/client';
import { mutationKeys } from '../mutation-keys';
import { queryKeys } from '../query-keys';

type MutationContext = { previousCalculation?: LiquidityCalculatorCalculationDto };

export const useUpdateLiquidityCalculatorCalculation = (): UseMutationResult<
    LiquidityCalculatorCalculationDto,
    AxiosError,
    { id: number } & LiquidityCalculatorPreferencesUpdateDto,
    MutationContext
> => {
    const queryClient = useQueryClient();
    const { data: client } = useClient();
    const calculationQueryKey = queryKeys.liquidityCalculatorCalculation(client?.partnerUuid);

    return useMutation({
        mutationKey: mutationKeys.updateLiquidityCalculatorCalculation(),
        mutationFn: ({ id, ...body }) => updateLiquidityCalculatorCalculation({ path: { id }, body }).then((result) => result.data!),
        // Optimistically patch the editable preference fields so controls react instantly. Server-computed metrics still
        // arrive with the response (onSuccess); the server stays the source of truth.
        onMutate: async ({ id: _id, ...preferences }) => {
            await queryClient.cancelQueries({ queryKey: calculationQueryKey });
            const previousCalculation = queryClient.getQueryData<LiquidityCalculatorCalculationDto>(calculationQueryKey);

            if (previousCalculation) {
                queryClient.setQueryData<LiquidityCalculatorCalculationDto>(calculationQueryKey, {
                    ...previousCalculation,
                    preferences: { ...previousCalculation.preferences, ...preferences },
                });
            }

            return { previousCalculation };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousCalculation) queryClient.setQueryData(calculationQueryKey, context.previousCalculation);
        },
        onSuccess: (data) => {
            queryClient.setQueryData(calculationQueryKey, data);
            queryClient.invalidateQueries({ queryKey: queryKeys.liquidityOverview(client?.partnerUuid) });
        },
    });
};
